import { useEffect, useRef, useState } from "react";
import type { Dispatch, RefObject, SetStateAction } from "react";
import { MarkdownContent } from "@/components/ui/markdown-content";
import { cn } from "@/lib/cn";
import { useT } from "@/i18n";
import { panelsTasksMessages } from "@/i18n/messages/panels-tasks";

/**
 * The task title on its painted card, in the card's display type, edited
 * in place. Escape cancels the edit only.
 */
export function TaskTitleSection({
  title,
  isEditingTitle,
  titleDraft,
  titleInputRef,
  setTitleDraft,
  setIsEditingTitle,
  onSaveTitle,
}: {
  title: string;
  isEditingTitle: boolean;
  titleDraft: string;
  titleInputRef: RefObject<HTMLInputElement | null>;
  setTitleDraft: Dispatch<SetStateAction<string>>;
  setIsEditingTitle: Dispatch<SetStateAction<boolean>>;
  onSaveTitle: () => void;
}) {
  const t = useT(panelsTasksMessages);
  return isEditingTitle ? (
    <input
      ref={titleInputRef}
      type="text"
      value={titleDraft}
      aria-label={t("title")}
      onChange={(event) => setTitleDraft(event.target.value)}
      onBlur={onSaveTitle}
      onKeyDown={(event) => {
        if (event.key === "Enter") onSaveTitle();
        if (event.key === "Escape") {
          event.preventDefault();
          setIsEditingTitle(false);
        }
      }}
      className="-mx-[0.2em] block w-[calc(100%+0.4em)] rounded-control bg-[var(--card-paint-task-chip)] px-[0.2em] outline-none"
      data-testid="task-pocket-title-input"
    />
  ) : (
    <button
      type="button"
      onClick={() => {
        setTitleDraft(title);
        setIsEditingTitle(true);
      }}
      className="-mx-[0.2em] block w-[calc(100%+0.4em)] rounded-control px-[0.2em] text-left transition-colors [text-wrap:balance] hover:bg-[var(--card-paint-task-chip)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
      data-testid="task-pocket-title"
    >
      {title}
    </button>
  );
}

/** The description, 13px, folded after five lines with Show more. */
export function TaskDescriptionSection({
  description,
  isEditingDesc,
  descDraft,
  descInputRef,
  setDescDraft,
  setIsEditingDesc,
  onSaveDescription,
}: {
  description: string | null | undefined;
  isEditingDesc: boolean;
  descDraft: string;
  descInputRef: RefObject<HTMLTextAreaElement | null>;
  setDescDraft: Dispatch<SetStateAction<string>>;
  setIsEditingDesc: Dispatch<SetStateAction<boolean>>;
  onSaveDescription: () => void;
}) {
  const t = useT(panelsTasksMessages);
  const contentRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  useEffect(() => {
    const content = contentRef.current;
    // Expanded content never overflows; keep the last folded measurement.
    if (!content || expanded) return;
    const measure = () =>
      setOverflows(content.scrollHeight > content.clientHeight + 1);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(content);
    return () => observer.disconnect();
  }, [description, expanded, isEditingDesc]);

  return (
    <div className="px-1 pt-2.5">
      {isEditingDesc ? (
        <textarea
          ref={descInputRef}
          value={descDraft}
          aria-label={t("description")}
          onChange={(event) => setDescDraft(event.target.value)}
          onBlur={onSaveDescription}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              setIsEditingDesc(false);
              setDescDraft(description ?? "");
            }
          }}
          onInput={(event) => {
            const field = event.currentTarget;
            field.style.height = "auto";
            field.style.height = `${field.scrollHeight}px`;
          }}
          className="block w-full resize-none overflow-hidden rounded-control bg-input-fill px-1 py-0.5 text-appkit-body text-primary outline-none"
          data-testid="task-pocket-description-input"
        />
      ) : (
        <>
          <button
            type="button"
            onClick={() => {
              setDescDraft(description ?? "");
              setIsEditingDesc(true);
            }}
            className="block w-full rounded-control px-1 py-0.5 text-left text-appkit-body transition-colors hover:bg-[var(--color-interactive-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            data-testid="task-pocket-description"
          >
            {description ? (
              <div
                ref={contentRef}
                className={cn(!expanded && "max-h-[5lh] overflow-hidden")}
              >
                <MarkdownContent
                  content={description}
                  className="text-appkit-body text-primary [&_h2]:mt-2 [&_h2]:font-semibold"
                />
              </div>
            ) : (
              <span className="text-muted">{t("addDescription")}</span>
            )}
          </button>
          {description && overflows ? (
            <button
              type="button"
              onClick={() => setExpanded((value) => !value)}
              aria-expanded={expanded}
              className="px-1 text-appkit-caption font-medium text-muted transition-colors hover:text-primary"
              data-testid="task-pocket-description-toggle"
            >
              {expanded ? t("showLess") : t("showMore")}
            </button>
          ) : null}
        </>
      )}
    </div>
  );
}
